import { useCallback, useEffect, useRef, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { IconButton } from "@atlaskit/button-next/new";
import CrossIcon from "@atlaskit/icon/core/cross";
import { JiraIcon } from "@atlaskit/logo-next";
import { Flex } from "@atlaskit/primitives-next/compiled";

import { wacToken } from "@/libraries/design/foundations";
import { messages } from "@/templates/paid-landing/sections/cfind-4666-frictionless-first-look/messages.ts";
import { useJiraAiFFL } from "@/templates/paid-landing/src/hooks/use-jira-ai-ffl-context";

import { PaidJiraAiTeamTypeMadLibPrompt } from "../paid-jira-ai-team-type-mad-lib-prompt";

import { Background } from "./background";
import { Header } from "./header";
import { PreviewTable } from "./preview-table";

import type { TableRowProps } from "./preview-table/table-row";

export interface PaidJiraAiPreviewModalProps {
    onClose?: () => void;
}

const REVEAL_INTERVAL_MS = 250;
type PreviewRow = Omit<TableRowProps, "isLastRow">;

const containerStyle = css({
    position: "fixed",
    insetInlineStart: 0,
    insetBlockStart: 0,
    width: "100vw",
    height: "100vh",
    background: wacToken("color.neutral.light0"),
    zIndex: 20,
    padding: "20px 28px"
});

export const PaidJiraAiPreviewModal = ({
    onClose
}: PaidJiraAiPreviewModalProps) => {
    const intl = useIntl();
    const {
        cgsDataLoading,
        formattedCgsData,
        rawCgsData,
        isErrorFromCgs,
        firePaidJiraAi,
        promptAttemptIndex
    } = useJiraAiFFL();
    const [showJiraSpaceReady, setShowJiraSpaceReady] = useState(false);
    const [progressiveRows, setProgressiveRows] = useState<
        Array<PreviewRow | undefined> | undefined
    >(undefined);
    const revealTimeoutsRef = useRef<number[]>([]);
    const modalOpenedAtRef = useRef<number | null>(null);
    const revealStartedAtRef = useRef<number | null>(null);
    const rowsRevealedCountRef = useRef(0);
    const revealPlannedMsRef = useRef<number | null>(null);

    const clearRevealTimeouts = useCallback(() => {
        revealTimeoutsRef.current.forEach((t) => window.clearTimeout(t));
        revealTimeoutsRef.current = [];
    }, []);

    useEffect(() => {
        modalOpenedAtRef.current = performance.now();
        firePaidJiraAi("PreviewModalOpened", "viewed", {
            promptAttemptIndex
        });

        // Cleanup on unmount.
        return () => clearRevealTimeouts();
    }, [clearRevealTimeouts, firePaidJiraAi, promptAttemptIndex]);

    // Reset UI while loading or on error.
    useEffect(() => {
        if (cgsDataLoading || isErrorFromCgs) {
            clearRevealTimeouts();
            setShowJiraSpaceReady(false);
            setProgressiveRows(undefined);
        }
    }, [cgsDataLoading, isErrorFromCgs, clearRevealTimeouts]);

    // Progressive reveal: append rows one-by-one so customers see "AI generating" behavior.
    // Note: this is a UI effect; the API response still arrives as a single payload.
    useEffect(() => {
        if (cgsDataLoading || isErrorFromCgs) {
            return;
        }

        if (!formattedCgsData || formattedCgsData.length === 0) {
            return;
        }

        clearRevealTimeouts();
        setShowJiraSpaceReady(false);
        rowsRevealedCountRef.current = 0;
        revealStartedAtRef.current = performance.now();
        revealPlannedMsRef.current =
            formattedCgsData.length * REVEAL_INTERVAL_MS;

        firePaidJiraAi("PreviewRevealStarted", "started", {
            promptAttemptIndex,
            rowCount: formattedCgsData.length,
            revealPlannedMs: revealPlannedMsRef.current
        });

        // Keep a fixed height while revealing (shows placeholder rows).
        const minRowCount = 10;
        const targetLen = Math.max(minRowCount, formattedCgsData.length);
        setProgressiveRows(Array.from({ length: targetLen }));

        formattedCgsData.forEach((row, idx) => {
            const t = window.setTimeout(() => {
                setProgressiveRows((prev) => {
                    if (!prev) {
                        return prev;
                    }
                    const next = [...prev];
                    next[idx] = row;
                    rowsRevealedCountRef.current = Math.max(
                        rowsRevealedCountRef.current,
                        idx + 1
                    );
                    return next;
                });
            }, idx * REVEAL_INTERVAL_MS);
            revealTimeoutsRef.current.push(t);
        });

        // Mark workspace ready only after the table finishes populating.
        const done = window.setTimeout(() => {
            setProgressiveRows(formattedCgsData);
            setShowJiraSpaceReady(true);

            const now = performance.now();
            const revealDurationMs = revealStartedAtRef.current
                ? Math.max(0, now - revealStartedAtRef.current)
                : undefined;
            const dwellMs = modalOpenedAtRef.current
                ? Math.max(0, now - modalOpenedAtRef.current)
                : undefined;

            firePaidJiraAi("PreviewReady", "succeeded", {
                promptAttemptIndex,
                rowCount: formattedCgsData.length,
                revealDurationMs,
                revealPlannedMs: revealPlannedMsRef.current ?? undefined,
                dwellMs
            });
        }, formattedCgsData.length * REVEAL_INTERVAL_MS + 50);
        revealTimeoutsRef.current.push(done);
    }, [
        cgsDataLoading,
        isErrorFromCgs,
        formattedCgsData,
        clearRevealTimeouts,
        firePaidJiraAi,
        promptAttemptIndex
    ]);

    const closeMessage = intl.formatMessage(messages.close);

    const handleClose = useCallback(() => {
        const now = performance.now();
        const dwellMs = modalOpenedAtRef.current
            ? Math.max(0, now - modalOpenedAtRef.current)
            : undefined;

        firePaidJiraAi("PreviewModalClosed", "closed", {
            promptAttemptIndex,
            closeReason: "xButton",
            dwellMs,
            rowsRevealedCount: rowsRevealedCountRef.current
        });
        onClose?.();
    }, [firePaidJiraAi, onClose, promptAttemptIndex]);

    return (
        <div css={containerStyle}>
            <Background />
            <Flex alignItems="center" justifyContent="space-between">
                <JiraIcon appearance="brand" shouldUseNewLogoDesign />
                <IconButton
                    shape="circle"
                    icon={CrossIcon}
                    label={closeMessage}
                    appearance="default"
                    onClick={handleClose}
                />
            </Flex>
            {showJiraSpaceReady && (
                <Header
                    onRetryClick={() => {
                        firePaidJiraAi("PreviewRetryClicked", "clicked", {
                            promptAttemptIndex,
                            rowsRevealedCount: rowsRevealedCountRef.current
                        });
                        clearRevealTimeouts();
                        setShowJiraSpaceReady(false);
                        setProgressiveRows(undefined);
                    }}
                />
            )}
            <PreviewTable
                rows={progressiveRows ?? formattedCgsData}
                spaceName={
                    cgsDataLoading
                        ? undefined
                        : rawCgsData?.sampleIssues.spaceName
                }
            />
            {!showJiraSpaceReady && <PaidJiraAiTeamTypeMadLibPrompt isRerun />}
        </div>
    );
};
